// ═══════════════════════════════════════════════════════════════════════════════
// CENTRALIZED COLOR TOKENS — Vibrant Ayurvedic Green Yoga Theme
// ═══════════════════════════════════════════════════════════════════════════════
//
// Design direction:
//   Fresh, energetic, modern wellness — combining emerald green, fresh leaf
//   green, pale mint backgrounds, clean white surfaces and restrained gold.
//
// Brand hierarchy:
//   • Emerald Green — primary brand, headings, buttons and key actions
//   • Fresh Green   — hover states, links and secondary emphasis
//   • Deep Emerald  — navigation, footer and dark sections
//   • Mint Green    — backgrounds, cards and wellness surfaces
//   • Golden Yellow — small premium accents only
//
// IMPORTANT:
//   Existing export names and object shapes are preserved for compatibility.
//
// ═══════════════════════════════════════════════════════════════════════════════


// ─── 1. RAW PALETTE ──────────────────────────────────────────────────────────

export const COLORS = {

  // ── Primary — vibrant emerald green ─────────────────────────────────────────
  primary:       "#2E7D32",
  primaryLight:  "#43A047",
  primaryDark:   "#1B5E20",

  // ── Accent — fresh leaf + restrained gold ───────────────────────────────────
  accent:        "#66BB6A",
  accentDark:    "#388E3C",
  accentWarm:    "#F9A825",

  // ── Backgrounds — bright, fresh and breathable ──────────────────────────────
  bgWarm:        "#F1F8F1",
  bgCool:        "#E8F5E9",
  bgLight:       "#C8E6C9",
  bgWarmLight:   "#FFF8E1",

  // ── Surfaces ────────────────────────────────────────────────────────────────
  white:         "#FFFFFF",
  pureWhite:     "#FFFFFF",

  // ── Text — deep green rather than harsh black ───────────────────────────────
  textDark:      "#173B20",
  textBody:      "#405447",
  textMid:       "#687A6C",
  textSoft:      "#A7B4AA",

  // ── Semi-transparent surfaces ──────────────────────────────────────────────
  surface72:     "rgba(255, 255, 255, 0.72)",
  surface82:     "rgba(255, 255, 255, 0.82)",
  surface92:     "rgba(255, 255, 255, 0.92)",
  surface98:     "rgba(255, 255, 255, 0.98)",

  // ── Semi-transparent borders ───────────────────────────────────────────────
  border:        "rgba(46, 125, 50, 0.18)",
  borderWhite80: "rgba(255, 255, 255, 0.80)",
  borderWhite85: "rgba(255, 255, 255, 0.85)",
  borderWhite88: "rgba(255, 255, 255, 0.88)",
  borderGold20:  "rgba(249, 168, 37, 0.20)",

  // ── Tints / Glows ──────────────────────────────────────────────────────────
  primaryTint:   "rgba(46, 125, 50, 0.08)",
  primaryGlow:   "rgba(46, 125, 50, 0.22)",
  primaryShadow: "rgba(46, 125, 50, 0.15)",
  accentTint:    "rgba(102, 187, 106, 0.12)",
  accentGlow:    "rgba(102, 187, 106, 0.25)",

  // ── Overlay ─────────────────────────────────────────────────────────────────
  overlay:       "rgba(23, 59, 32, 0.40)",

  // ── Supporting / semantic ──────────────────────────────────────────────────
  muted:         "#E8F5E9",
  star:          "#F9A825",
  error:         "#C62828",
  pageBg:        "#F1F8F1",


  // ── Diet Planner — aligned with main YogaShastra brand ─────────────────────
  dietPrimary:      "#2E7D32",
  dietSecondary:    "#81C784",
  dietAccent:       "#F9A825",
  dietBg:           "#FAFAF8",
  dietSurface:      "#FFFFFF",
  dietText:         "#222222",
  dietTextMuted:    "#666666",
  dietError:        "#C62828",

  // ── Diet Planner supplemental tokens ────────────────────────────────────────
  dietPrimaryTint:  "#E8F5E9",
  dietAccentTint:   "#FFF8E1",
};


// ─── 2. THEME-PROP FALLBACKS ─────────────────────────────────────────────────

export const THEME_FALLBACKS = {
  buttonColor:    COLORS.primary,
  TextColorBlack: COLORS.textDark,
  lightBlueShade: COLORS.bgCool,
  yellowShade:    COLORS.bgLight,
};


// ─── 3. CSS CUSTOM-PROPERTY REFERENCES ──────────────────────────────────────

export const CSS_VARS = {
  accent:       "var(--c-accent)",
  accent2:      "var(--c-accent-2)",
  accent3:      "var(--c-accent-3)",
  text:         "var(--c-text)",
  white:        "var(--c-white)",
  surface:      "var(--c-surface)",
  surfaceHigh:  "var(--c-surface-high)",
  surfaceHi:    "var(--c-surface-hi)",
  border:       "var(--c-border)",
  borderAccent: "var(--c-border-accent)",
  tint:         "var(--c-tint)",
  glow:         "var(--c-glow)",
  shadow:       "var(--c-shadow)",
  overlay:      "var(--c-overlay)",
  accentTint:   "var(--c-accent-tint)",
  accentGlow:   "var(--c-accent-glow)",
  bgFrom:       "var(--c-bg-from)",
  bgTo:         "var(--c-bg-to)",
};


// ─── 4. SECTION-LEVEL CSS CUSTOM PROPERTIES ─────────────────────────────────
// Primary vibrant green theme.
// Existing export name retained for compatibility.

export const INDIGO_CSS_VARS = `
  --c-accent:        ${COLORS.primary};
  --c-accent-2:      ${COLORS.primaryDark};
  --c-accent-3:      ${COLORS.accentDark};
  --c-white:         ${COLORS.white};
  --c-surface:       ${COLORS.surface82};
  --c-surface-hi:    ${COLORS.surface98};
  --c-surface-high:  ${COLORS.surface92};
  --c-border:        ${COLORS.borderWhite88};
  --c-border-accent: ${COLORS.borderGold20};
  --c-tint:          ${COLORS.primaryTint};
  --c-glow:          ${COLORS.primaryGlow};
  --c-shadow:        ${COLORS.primaryShadow};
  --c-overlay:       ${COLORS.overlay};
  --c-accent-tint:   ${COLORS.primaryTint};
  --c-accent-glow:   ${COLORS.primaryGlow};
`;


// ─── 5. GREEN CSS CUSTOM PROPERTIES ─────────────────────────────────────────
// Existing export name retained for compatibility.

export const GREEN_CSS_VARS = `
  --c-accent:        ${COLORS.primary};
  --c-accent-2:      ${COLORS.accent};
  --c-white:         ${COLORS.white};
  --c-surface:       ${COLORS.surface72};
  --c-surface-high:  ${COLORS.surface92};
  --c-border:        ${COLORS.borderWhite85};
  --c-border-accent: ${COLORS.borderGold20};
  --c-overlay:       ${COLORS.overlay};
  --c-accent-tint:   ${COLORS.primaryTint};
  --c-accent-glow:   ${COLORS.primaryGlow};
`;


// ─── 6. DIET PLANNER CSS CUSTOM PROPERTIES ──────────────────────────────────

export const DIET_CSS_VARS = `
  --c-primary:      ${COLORS.dietPrimary};
  --c-secondary:    ${COLORS.dietSecondary};
  --c-accent:       ${COLORS.dietAccent};
  --c-bg:           ${COLORS.dietBg};
  --c-surface:      ${COLORS.dietSurface};
  --c-text:         ${COLORS.dietText};
  --c-text-muted:   ${COLORS.dietTextMuted};
  --c-error:        ${COLORS.dietError};
  --c-border:       ${COLORS.dietSecondary};
`;


// ─── 7. PACKAGES / SLIDER DESIGN TOKENS ─────────────────────────────────────

export const BRAND_C = {
  brand:      COLORS.primary,
  brandLight: COLORS.primaryLight,
  brandDark:  COLORS.primaryDark,
  accent:     COLORS.accent,
  surface:    COLORS.white,
  surfaceAlt: COLORS.bgCool,
  border:     COLORS.border,
  text:       COLORS.textBody,
  textMid:    COLORS.textMid,
  textSoft:   COLORS.textSoft,
  muted:      COLORS.muted,
};


// ─── 8. DIET PLANNER JS TOKEN OBJECT ─────────────────────────────────────────

export const DIET_C = {
  primary:      COLORS.dietPrimary,
  secondary:    COLORS.dietSecondary,
  accent:       COLORS.dietAccent,
  background:   COLORS.dietBg,
  surface:      COLORS.dietSurface,
  text:         COLORS.dietText,
  textMuted:    COLORS.dietTextMuted,
  error:        COLORS.dietError,

  primaryLight: COLORS.dietSecondary,
  primaryTint:  COLORS.dietPrimaryTint,
  accentTint:   COLORS.dietAccentTint,
  white:        COLORS.dietSurface,
  border:       COLORS.dietSecondary,
};


// ─── 9. DIET PLANNER RGB TOKENS ─────────────────────────────────────────────

function hexToRgb(hex) {
  const clean = hex.replace("#", "");
  const bigint = parseInt(clean, 16);

  return [
    (bigint >> 16) & 255,
    (bigint >> 8) & 255,
    bigint & 255
  ];
}

export const DIET_C_RGB = Object.fromEntries(
  Object.entries(DIET_C).map(([key, hex]) => [key, hexToRgb(hex)])
);